import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Radio, Space, Spin, Table, Typography, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import axios from 'axios'
import { useHistory } from 'react-router-dom'

interface NetworkSection {
  key: string
  title: string
  items: string[]
  suffix: string
}

interface ConquestShowPdvInitData {
  orgaId: string
  softaccessId: string
  pdvToShow: string[]
  mainNetwork: string[]
  network1: string[]
  network2: string[]
  network3: string[]
  network4: string[]
  network5: string[]
}

interface ConquestSoftAccessShowPdvPageProps {
  orgaId: string
  softaccessId: string
}

interface PdvTableRow {
  key: string
  name: string
}

function PdvNetworkSection({
  title,
  items,
  suffix,
  pdvToShowSet,
  onChange,
  bulkSelect,
}: {
  title?: string
  items: string[]
  suffix: string
  pdvToShowSet: Set<string>
  onChange: (name: string, visible: boolean) => void
  bulkSelect: (suffix: string, visible: boolean) => void
}) {
  if (items.length === 0) return null

  const dataSource = useMemo<PdvTableRow[]>(
    () => items.map(item => ({ key: `${item}-${suffix}`, name: item })),
    [items, suffix],
  )

  const columns = useMemo<ColumnsType<PdvTableRow>>(
    () => [
      {
        title: 'PDV',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name, 'fr'),
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: 'Afficher ?',
        key: 'visible',
        width: 180,
        render: (_, record) => (
          <Radio.Group
            value={pdvToShowSet.has(record.name) ? 'true' : 'false'}
            onChange={e => onChange(record.name, e.target.value === 'true')}
          >
            <Radio value="true">Oui</Radio>
            <Radio value="false">Non</Radio>
          </Radio.Group>
        ),
      },
    ],
    [onChange, pdvToShowSet],
  )

  return (
    <div style={{ marginBottom: 32 }}>
      {title && (
        <Space align="center" style={{ marginBottom: 12 }} wrap>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {title}
          </Typography.Title>
          <Button size="small" onClick={() => bulkSelect(suffix, true)}>
            Tout oui
          </Button>
          <Button size="small" onClick={() => bulkSelect(suffix, false)}>
            Tout non
          </Button>
        </Space>
      )}
      <Table
        rowKey="key"
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        size="small"
        bordered
      />
    </div>
  )
}

export function ConquestSoftAccessShowPdvPage({
  orgaId,
  softaccessId,
}: ConquestSoftAccessShowPdvPageProps) {
  const history = useHistory()
  const [initData, setInitData] = useState<ConquestShowPdvInitData | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [pdvToShow, setPdvToShow] = useState<Set<string>>(new Set())

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const { data } = await axios.get<ConquestShowPdvInitData>(
          `/organisations/${orgaId}/conquest/softwaresaccesses/${softaccessId}/access/init`,
        )
        setInitData(data)
        setPdvToShow(new Set(data.pdvToShow))
      } catch (error) {
        console.error(error)
        message.error('Impossible de charger les accès pdv rg4')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [orgaId, softaccessId])

  const sections = useMemo<NetworkSection[]>(() => {
    if (!initData) return []
    return [
      { key: 'main', title: 'Réseau principal', items: initData.mainNetwork, suffix: 'network0' },
      { key: 'network1', title: 'Réseau secondaire 1', items: initData.network1, suffix: 'network1' },
      { key: 'network2', title: 'Réseau secondaire 2', items: initData.network2, suffix: 'network2' },
      { key: 'network3', title: 'Réseau secondaire 3', items: initData.network3, suffix: 'network3' },
      { key: 'network4', title: 'Réseau secondaire 4', items: initData.network4, suffix: 'network4' },
      { key: 'network5', title: 'Réseau secondaire 5', items: initData.network5, suffix: 'network5' },
    ].filter(section => section.items.length > 0)
  }, [initData])

  const allItems = useMemo(
    () => sections.flatMap(section => section.items),
    [sections],
  )

  const handleItemChange = useCallback((name: string, visible: boolean) => {
    setPdvToShow(prev => {
      const next = new Set(prev)
      if (visible) next.add(name)
      else next.delete(name)
      return next
    })
  }, [])

  const bulkSelectAll = useCallback((visible: boolean) => {
    setPdvToShow(visible ? new Set(allItems) : new Set())
  }, [allItems])

  const bulkSelectSection = useCallback((suffix: string, visible: boolean) => {
    const section = sections.find(item => item.suffix === suffix)
    if (!section) return
    setPdvToShow(prev => {
      const next = new Set(prev)
      section.items.forEach(item => {
        if (visible) next.add(item)
        else next.delete(item)
      })
      return next
    })
  }, [sections])

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      const body = new URLSearchParams()
      body.set('data', Array.from(pdvToShow).join(','))
      await axios.post(
        `/organisations/${orgaId}/conquest/softwaresaccesses/${softaccessId}/access`,
        body,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
      )
      message.success('Accès pdv enregistrés')
      history.push(`/organisations/${orgaId}/softwares`)
    } catch (error) {
      console.error(error)
      message.error("Erreur lors de l'enregistrement")
    } finally {
      setSaving(false)
    }
  }, [history, orgaId, pdvToShow, softaccessId])

  if (loading || !initData) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  const hasSecondaryNetworks = sections.some(section => section.key !== 'main')

  return (
    <div style={{ marginTop: 16 }}>
      <Typography.Title level={4}>Accès pdv rang 4</Typography.Title>

      <Space style={{ marginBottom: 24 }} wrap>
        <Button onClick={() => bulkSelectAll(true)}>Tout oui</Button>
        <Button onClick={() => bulkSelectAll(false)}>Tout non</Button>
      </Space>

      {sections.map(section => (
        <PdvNetworkSection
          key={section.key}
          title={section.key === 'main' && hasSecondaryNetworks ? section.title : section.key !== 'main' ? section.title : undefined}
          items={section.items}
          suffix={section.suffix}
          pdvToShowSet={pdvToShow}
          onChange={handleItemChange}
          bulkSelect={bulkSelectSection}
        />
      ))}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, margin: '24px 0' }}>
        <Button onClick={() => history.push(`/organisations/${orgaId}/softwares`)}>Annuler</Button>
        <Button type="primary" loading={saving} onClick={handleSave}>
          Enregistrer
        </Button>
      </div>
    </div>
  )
}
